import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  getDocs, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  serverTimestamp,
  where
} from 'firebase/firestore';
import { FIREBASE_DB } from '../firebase.config';
import { Edit, Trash, PlusCircle, Clock, EyeOff, Eye, Pin, FileText, Users, User } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const BulletinManagement = () => {
  const [bulletins, setBulletins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [currentBulletin, setCurrentBulletin] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initial form state
  const initialBulletinState = {
    title: '',
    content: '',
    isPublic: true,
    isActive: true,
    isPinned: false,
    category: 'general',
    expiresAt: null,
  };

  // Fetch bulletins from Firestore
  const fetchBulletins = async () => {
    try {
      setLoading(true);
      
      // For admin users, get all bulletins
      const bulletinsQuery = query(
        collection(FIREBASE_DB, 'bulletins'),
        orderBy('isPinned', 'desc'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(bulletinsQuery);
      const bulletinsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        expiresAt: doc.data().expiresAt?.toDate()
      }));
      
      setBulletins(bulletinsList);
    } catch (error) {
      console.error('Error fetching bulletins:', error);
      alert('Error fetching bulletins. Please ensure you have proper permissions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBulletins();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentBulletin(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle expiration date change
  const handleExpiryChange = (e) => {
    const date = e.target.value;
    if (date) {
      setCurrentBulletin(prev => ({
        ...prev,
        expiresAt: new Date(date)
      }));
    } else {
      setCurrentBulletin(prev => ({
        ...prev,
        expiresAt: null
      }));
    }
  };

  // Start editing a bulletin
  const handleEdit = (bulletin) => {
    setCurrentBulletin({
      ...bulletin,
      expiresAt: bulletin.expiresAt ? formatDateForInput(bulletin.expiresAt) : ''
    });
    setEditMode(true);
  };

  // Format date for input field
  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  // Start creating a new bulletin
  const handleCreateNew = () => {
    setCurrentBulletin({
      ...initialBulletinState
    });
    setEditMode(true);
  };

  // Cancel editing/creating
  const handleCancel = () => {
    setEditMode(false);
    setCurrentBulletin(null);
  };

  // Handle form submission (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentBulletin) return;
    
    try {
      setIsSubmitting(true);
      
      const bulletinData = {
        ...currentBulletin,
        updatedAt: serverTimestamp(),
        expiresAt: currentBulletin.expiresAt ? serverTimestamp(currentBulletin.expiresAt) : null
      };
      
      delete bulletinData.id; // Remove id before saving to Firestore
      
      if (currentBulletin.id) {
        // Update existing bulletin
        await updateDoc(doc(FIREBASE_DB, 'bulletins', currentBulletin.id), bulletinData);
      } else {
        // Create new bulletin
        bulletinData.createdAt = serverTimestamp();
        await addDoc(collection(FIREBASE_DB, 'bulletins'), bulletinData);
      }
      
      // Reset form and refresh list
      setEditMode(false);
      setCurrentBulletin(null);
      await fetchBulletins();
      
    } catch (error) {
      console.error('Error saving bulletin:', error);
      alert('Failed to save bulletin. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete a bulletin
  const handleDelete = async (bulletin) => {
    if (!window.confirm(`Are you sure you want to delete the bulletin "${bulletin.title || 'Untitled'}"?`)) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Delete the bulletin document
      await deleteDoc(doc(FIREBASE_DB, 'bulletins', bulletin.id));
      
      // Refresh the list
      await fetchBulletins();
      
    } catch (error) {
      console.error('Error deleting bulletin:', error);
      alert('Failed to delete bulletin. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Toggle bulletin active status
  const toggleActive = async (bulletin) => {
    try {
      await updateDoc(doc(FIREBASE_DB, 'bulletins', bulletin.id), {
        isActive: !bulletin.isActive,
        updatedAt: serverTimestamp()
      });
      await fetchBulletins();
    } catch (error) {
      console.error('Error toggling bulletin status:', error);
      alert('Failed to update bulletin status. Please try again.');
    }
  };

  // Toggle bulletin pinned status
  const togglePinned = async (bulletin) => {
    try {
      await updateDoc(doc(FIREBASE_DB, 'bulletins', bulletin.id), {
        isPinned: !bulletin.isPinned,
        updatedAt: serverTimestamp()
      });
      await fetchBulletins();
    } catch (error) {
      console.error('Error toggling pinned status:', error);
      alert('Failed to update bulletin pinned status. Please try again.');
    }
  };

  // Check if bulletin is expired
  const isExpired = (bulletin) => {
    if (!bulletin.expiresAt) return false;
    return new Date() > new Date(bulletin.expiresAt);
  };

  // Get status label for a bulletin
  const getBulletinStatusLabel = (bulletin) => {
    if (!bulletin.isActive) return 'Inactive';
    if (isExpired(bulletin)) return 'Expired';
    return 'Active';
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Bulletin Management</h2>
        {!editMode && (
          <button
            onClick={handleCreateNew}
            className="flex items-center bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition-colors"
          >
            <PlusCircle size={18} className="mr-2" />
            Add New Bulletin
          </button>
        )}
      </div>

      {editMode ? (
        // Bulletin Edit Form
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                name="title"
                value={currentBulletin?.title || ''}
                onChange={handleInputChange}
                placeholder="Enter bulletin title"
                className="w-full rounded-md border-gray-300 shadow-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
              <textarea
                name="content"
                value={currentBulletin?.content || ''}
                onChange={handleInputChange}
                placeholder="Enter bulletin content"
                rows={6}
                className="w-full rounded-md border-gray-300 shadow-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                name="category"
                value={currentBulletin?.category || 'general'}
                onChange={handleInputChange}
                className="w-full rounded-md border-gray-300 shadow-sm"
              >
                <option value="general">General</option>
                <option value="announcement">Announcement</option>
                <option value="news">News</option>
                <option value="event">Event</option>
                <option value="policy">Policy</option>
                <option value="hr">HR</option>
                <option value="it">IT</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expiration Date (Optional)</label>
              <input
                type="date"
                value={currentBulletin?.expiresAt || ''}
                onChange={handleExpiryChange}
                className="w-full rounded-md border-gray-300 shadow-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Leave blank for no expiration</p>
            </div>

            <div className="flex flex-col space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isPublic"
                  name="isPublic"
                  checked={currentBulletin?.isPublic || false}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-accent focus:ring-accent border-gray-300 rounded"
                />
                <label htmlFor="isPublic" className="ml-2 block text-sm text-gray-700">
                  Public Bulletin (Visible to everyone on the website)
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={currentBulletin?.isActive || false}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-accent focus:ring-accent border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                  Active (Display on Website/Portal)
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isPinned"
                  name="isPinned"
                  checked={currentBulletin?.isPinned || false}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-accent focus:ring-accent border-gray-300 rounded"
                />
                <label htmlFor="isPinned" className="ml-2 block text-sm text-gray-700">
                  Pinned (Show at the top of the list)
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={handleCancel}
              className="bg-gray-200 text-gray-800 px-6 py-2 rounded-md hover:bg-gray-300 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark transition-colors"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : currentBulletin?.id ? 'Update Bulletin' : 'Create Bulletin'}
            </button>
          </div>
        </form>
      ) : (
        // Bulletin list
        <div>
          {loading ? (
            <p className="text-center py-8 text-gray-500">Loading bulletins...</p>
          ) : bulletins.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-gray-300 rounded-lg">
              <FileText size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">No bulletins found</p>
              <button
                onClick={handleCreateNew}
                className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition-colors"
              >
                Create Your First Bulletin
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title & Content
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Audience
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bulletins.map((bulletin) => (
                    <tr key={bulletin.id} className={`hover:bg-gray-50 ${isExpired(bulletin) && bulletin.isActive ? 'bg-red-50' : ''}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-start">
                          {bulletin.isPinned && (
                            <Pin size={16} className="text-amber-500 mr-2 mt-1 flex-shrink-0" />
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900">{bulletin.title || 'Untitled Bulletin'}</div>
                            <div className="text-sm text-gray-500 mt-1 line-clamp-2">{bulletin.content}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800`}>
                          {bulletin.category || 'general'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          !bulletin.isActive 
                            ? 'bg-gray-100 text-gray-800' 
                            : isExpired(bulletin)
                              ? 'bg-red-100 text-red-800'
                              : 'bg-green-100 text-green-800'
                        }`}>
                          {getBulletinStatusLabel(bulletin)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {bulletin.isPublic ? (
                            <div className="flex items-center text-green-600">
                              <Users size={16} className="mr-1" />
                              <span className="text-xs">Public</span>
                            </div>
                          ) : (
                            <div className="flex items-center text-purple-600">
                              <User size={16} className="mr-1" />
                              <span className="text-xs">Employees Only</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex flex-col">
                          <span>
                            {bulletin.createdAt 
                              ? formatDistanceToNow(new Date(bulletin.createdAt), { addSuffix: true }) 
                              : 'Unknown date'}
                          </span>
                          {bulletin.expiresAt && (
                            <span className={`text-xs mt-1 ${isExpired(bulletin) ? 'text-red-500' : 'text-gray-400'}`}>
                              <Clock size={12} className="inline mr-1" />
                              {isExpired(bulletin) ? 'Expired' : 'Expires'}: {new Date(bulletin.expiresAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => togglePinned(bulletin)}
                            className={`p-1.5 rounded ${
                              bulletin.isPinned 
                                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' 
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            title={bulletin.isPinned ? 'Unpin' : 'Pin'}
                          >
                            <Pin size={16} />
                          </button>
                          <button
                            onClick={() => toggleActive(bulletin)}
                            className={`p-1.5 rounded ${
                              bulletin.isActive 
                                ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' 
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                            title={bulletin.isActive ? 'Deactivate' : 'Activate'}
                          >
                            {bulletin.isActive ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                          <button
                            onClick={() => handleEdit(bulletin)}
                            className="p-1.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                            title="Edit"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(bulletin)}
                            className="p-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200"
                            title="Delete"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BulletinManagement; 